import { type Language } from '@aikoa/i18n'
import { useCurrentLanguage } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Asset, type AssetMetadata } from 'expo-asset'
import { Suspense } from 'react'
import Markdown from 'react-markdown'
import { View } from 'react-native'
import { LoadingView } from '~/components/LoadingView.tsx'
import { Body } from '~/components/typography/Content.tsx'
import { Heading1, Heading2, Heading3 } from '~/components/typography/Titles.tsx'
import { cn } from '~/lib/utils.ts'

const contentMap = {
  'privacy-policy': {
    fi: require('~/../assets/content/fi/privacyPolicy.md'),
    en: require('~/../assets/content/en/privacyPolicy.md'),
    sv: require('~/../assets/content/sv/privacyPolicy.md'),
    es: require('~/../assets/content/es/privacyPolicy.md'),
  },
  'terms-of-service': {
    fi: require('~/../assets/content/fi/termsOfService.md'),
    en: require('~/../assets/content/en/termsOfService.md'),
    sv: require('~/../assets/content/sv/termsOfService.md'),
    es: require('~/../assets/content/es/termsOfService.md'),
  },
  dpa: {
    fi: require('~/../assets/content/fi/dpa.md'),
    en: require('~/../assets/content/en/dpa.md'),
    sv: require('~/../assets/content/sv/dpa.md'),
    es: require('~/../assets/content/es/dpa.md'),
  },
  'cookie-policy': {
    fi: require('~/../assets/content/fi/cookiePolicy.md'),
    en: require('~/../assets/content/en/cookiePolicy.md'),
    sv: require('~/../assets/content/sv/cookiePolicy.md'),
    es: require('~/../assets/content/es/cookiePolicy.md'),
  },
}

export default function MarkdownContent({
  contentKey,
  className,
}: {
  contentKey: keyof typeof contentMap
  className?: string
}) {
  const language = useCurrentLanguage()

  return (
    <Suspense fallback={<LoadingView />}>
      <MarkdownContentRenderer contentKey={contentKey} className={className} language={language} />
    </Suspense>
  )
}

function MarkdownContentRenderer({
  contentKey,
  className,
  language = 'fi',
}: {
  contentKey: keyof typeof contentMap
  className?: string
  language?: Language
}) {
  const { data } = useQuery({
    queryKey: ['content', contentKey, language],
    queryFn: async () => {
      const asset = Asset.fromModule(contentMap[contentKey][language])
      await asset.downloadAsync()
      const response = await fetch(resolveAssetUri(asset))
      return response.text()
    },
  })

  const content = data ?? ''

  if (!content) {
    return null
  }

  return (
    <View className={cn('w-full gap-3', className)}>
      <Markdown
        components={{
          div: ({ children }) => (
            <View className="flex flex-col gap-3 text-foreground">{children}</View>
          ),
          h1: ({ children }) => <Heading1 className="my-0 text-foreground">{children}</Heading1>,
          h2: ({ children }) => <Heading2 className="my-0 text-foreground">{children}</Heading2>,
          h3: ({ children }) => <Heading3 className="my-0 text-foreground">{children}</Heading3>,
          p: ({ children }) => (
            <View className="my-2">
              <Body className="text-foreground">{children}</Body>
            </View>
          ),
          li: ({ children }) => (
            <View className="my-2 flex-row items-start gap-2">
              <View className="mt-2 h-1.5 w-1.5 rounded-full bg-foreground" />
              <Body className="text-foreground">{children}</Body>
            </View>
          ),
        }}>
        {stripTags(content)}
      </Markdown>
    </View>
  )
}

function stripTags(content: string) {
  return content.replace(/<[^>]*>?/g, '')
}

function resolveAssetUri(asset: Asset | AssetMetadata) {
  if (!asset.uri) {
    throw new Error('Asset URI is missing')
  }

  if (typeof window === 'undefined') {
    return asset.uri
  }

  return new URL(asset.uri, window.location.origin).toString()
}
